import type { StudentProfileFormValues } from './types';

export type StudentProfileStepId = 'basic' | 'goals' | 'categories' | 'languages';

export type StudentProfileStepProgress = {
  id: StudentProfileStepId;
  filled: number;
  total: number;
  complete: boolean;
};

export type StudentProfileStepsState = {
  steps: StudentProfileStepProgress[];
  percent: number;
  firstIncompleteId: StudentProfileStepId | null;
};

function filled(value: string | null | undefined): boolean {
  return Boolean(value?.trim());
}

function countFilled(checks: boolean[]): { filled: number; total: number } {
  return {
    filled: checks.filter(Boolean).length,
    total: checks.length,
  };
}

export function computeStudentProfileSteps(
  values: StudentProfileFormValues,
  options?: { hasAvatar?: boolean },
): StudentProfileStepsState {
  const hasAvatar = options?.hasAvatar ?? filled(values.avatarUrl);

  const basic = countFilled([filled(values.displayName), filled(values.bio)]);
  const goals = countFilled([filled(values.goals)]);
  const categories = countFilled([values.categoryIds.length > 0]);
  const languages = countFilled([values.nativeLanguages.length > 0]);

  const steps: StudentProfileStepProgress[] = [
    { id: 'basic', ...basic, complete: basic.filled === basic.total },
    { id: 'goals', ...goals, complete: goals.filled === goals.total },
    {
      id: 'categories',
      ...categories,
      complete: categories.filled === categories.total,
    },
    {
      id: 'languages',
      ...languages,
      complete: languages.filled === languages.total,
    },
  ];

  const extraChecks = [hasAvatar, filled(values.timezone), filled(values.introVideoUrl)];
  const allChecks = [
    ...[basic, goals, categories, languages].flatMap((step) =>
      Array.from({ length: step.total }, (_, index) => index < step.filled),
    ),
    ...extraChecks,
  ];
  const done = allChecks.filter(Boolean).length;
  const percent = Math.round((done / allChecks.length) * 100);
  const firstIncomplete = steps.find((step) => !step.complete)?.id ?? null;

  return {
    steps,
    percent: Math.min(100, percent),
    firstIncompleteId: firstIncomplete,
  };
}
